import { ComponentProps, useState } from 'react';

import { ChevronDownIcon, ChevronSelectorVerticalIcon, ChevronUpIcon } from '@/assets/icons';
import { mergeClasses } from '@/utils';

import { SortDirection } from './interfaces';

const TableHead = ({
  className,
  sortable = false,
  sortDirection: controlledSortDirection,
  onSort,
  children,
  ...props
}: ComponentProps<'th'> & {
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: (direction: SortDirection) => void;
}) => {
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(null);

  // Determine if component is controlled
  const isControlled = controlledSortDirection !== undefined;

  // Use controlled sort direction if provided, otherwise use internal state
  const sortDirection = isControlled ? controlledSortDirection : internalSortDirection;

  const onClick = () => {
    if (!sortable) return;

    let newDirection: SortDirection;
    if (sortDirection === null) {
      newDirection = 'asc';
    } else if (sortDirection === 'asc') {
      newDirection = 'desc';
    } else {
      newDirection = null;
    }

    // Only update internal state if not controlled
    if (!isControlled) {
      setInternalSortDirection(newDirection);
    }
    onSort?.(newDirection);
  };

  const content = (
    <div className="inline-flex gap-2 items-center font-semibold text-body-sm text-neutral-8">
      {children}
      {sortable && (
        <span className="inline-flex text-primary-darkest">
          {sortDirection === null || sortDirection === undefined ? (
            <ChevronSelectorVerticalIcon className="size-base" />
          ) : sortDirection === 'asc' ? (
            <ChevronUpIcon className="size-base" />
          ) : (
            <ChevronDownIcon className="size-base" />
          )}
        </span>
      )}
    </div>
  );

  return (
    <th
      data-slot="table-head"
      className={mergeClasses(
        'text-foreground text-left align-middle font-medium whitespace-nowrap pl-6 border-b border-b-neutral-4',
        sortable ? 'cursor-pointer select-none hover:bg-neutral-2' : 'pr-6',
        className,
      )}
      onClick={onClick}
      {...props}
    >
      {content}
    </th>
  );
};

export default TableHead;
